import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import cambioEstadoService from '../../services/cambioEstadoService';
import { getAseguradoras } from '../../services/art/aseguradoraService';
import { obtenerSiguienteEstadoART } from '../../services/art/artEstadoService';
import { actualizarTramiteART } from '../../services/art/tramiteARTService';
import { subirArchivo, descargarArchivo, eliminarArchivo, obtenerArchivosPorTramite } from '../../services/archivoService';
import { listarMedicos } from '../../services/medicoService';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';

function GestorPreparandoTelegrama({ tramite, onAvanzar }) {
    const fileInputRef = useRef(null);

    const [aseguradoras, setAseguradoras] = useState([]);
    const [aseguradoraSeleccionada, setAseguradoraSeleccionada] = useState(null);
    const [medicos, setMedicos] = useState([]);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
    const [fechaEnvio, setFechaEnvio] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [cargando, setCargando] = useState(false);

    // Archivos
    const [archivos, setArchivos] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [cargandoArchivos, setCargandoArchivos] = useState(true);

    useEffect(() => {
        cargarArchivos();
        cargarAseguradoras();
        cargarMedicos();
    }, []);

    const cargarMedicos = async () => {
        try {
            const data = await listarMedicos(true); // true = solo activos
            setMedicos(data || []);
        } catch (error) {
            console.error("Error cargando médicos:", error);
            toast.error("No se pudieron cargar los médicos.");
        }
    };

    const cargarAseguradoras = async () => {
        try {
            const data = await getAseguradoras();
            setAseguradoras(data || []);
        } catch (error) {
            console.error("Error cargando aseguradoras:", error);
            toast.error("No se pudieron cargar las aseguradoras.");
        }
    };

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                esDelBackend: true
            }));
            setArchivos(archivosMapeados);
        } catch (error) {
            console.error("Error cargando archivos:", error);
            toast.error("No se pudieron cargar los archivos adjuntos.");
        } finally {
            setCargandoArchivos(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (e) => {
        const archivosSeleccionados = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            archivo: file,
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...archivosSeleccionados]);
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');

        if (archivosPendientes.length === 0) {
            toast.warn('No hay archivos pendientes para subir');
            return;
        }

        setSubiendoArchivos(true);
        try {
            for (const archivo of archivosPendientes) {
                try {
                    await subirArchivo(tramite.idTramite, archivo.archivo);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'subido' } : a)
                    );
                } catch (error) {
                    console.error(`Error subiendo ${archivo.nombre}:`, error);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'error' } : a)
                    );
                }
            }
            toast.success('Archivos procesados');
        } catch (error) {
            console.error('Error al subir archivos:', error);
            toast.error('Error al subir algunos archivos');
        } finally {
            setSubiendoArchivos(false);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        try {
            await descargarArchivo(archivo.idArchivo, archivo.nombre);
        } catch (error) {
            toast.error('Error al descargar archivo');
        }
    };

    const handleEliminarArchivo = async (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (!archivo) return;

        if (archivo.esDelBackend && archivo.idArchivo) {
            try {
                await eliminarArchivo(archivo.idArchivo);
                setArchivos(prev => prev.filter(a => a.id !== id));
                toast.success('Archivo eliminado');
            } catch (error) {
                toast.error('Error al eliminar archivo');
            }
        } else {
            setArchivos(prev => prev.filter(a => a.id !== id));
        }
    };

    const handleConfirmarEnvio = async () => {
        const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

        if (archivosPendientes) {
            toast.error('Debe subir todos los archivos pendientes primero');
            return;
        }

        if (!aseguradoraSeleccionada) {
            toast.warning('Debe seleccionar una Aseguradora (ART) antes de avanzar.');
            return;
        }

        if (!fechaEnvio) {
            toast.warning('Debe ingresar la fecha de envío del telegrama.');
            return;
        }

        setCargando(true);
        try {
            // PASO 1: Guardar SOLO la aseguradora en el trámite
            const tramiteParaActualizar = {
                idTipoTramite: Number(tramite.tipoTramite?.idTipoTramite || tramite.idTipoTramite || 2),
                idAseguradora: aseguradoraSeleccionada.idAseguradora,
                version: tramite.version
            };

            await actualizarTramiteART(tramite.idTramite, tramiteParaActualizar);
            toast.success(`Aseguradora ${aseguradoraSeleccionada.nombre} asociada correctamente`);

            // PASO 2: Cambiar el estado a "Telegrama Enviado" CON TODOS LOS DATOS EN EL COMENTARIO
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, 'Telegrama enviado');

            let comentarioFinal = `Telegrama enviado a ${aseguradoraSeleccionada.nombre}.\n`;
            comentarioFinal += `Fecha de envío: ${fechaEnvio}.\n`;

            if (medicoSeleccionado) {
                comentarioFinal += `Médico citado en el telegrama: ${medicoSeleccionado.nombre} ${medicoSeleccionado.apellido || ''}.\n`;
            }

            if (observaciones.trim()) {
                comentarioFinal += `Observaciones: ${observaciones.trim()}`;
            }

            const reqEstado = {
                idEstado: siguienteEstado.id,
                comentario: comentarioFinal
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, reqEstado);

            toast.success("Trámite avanzado a Telegrama Enviado");
            onAvanzar();
        } catch (error) {
            console.error('Error al procesar el trámite:', error);
            toast.error("Error al procesar el trámite: " + (error.response?.data?.message || error.message));
        } finally {
            setCargando(false);
        }
    };

    const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Preparando Telegrama</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <AssignmentTurnedInIcon fontSize="small" className="me-1" />
                    Gestión Legal
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Seleccione la Aseguradora de Riesgos de Trabajo (ART) correspondiente y opcionalmente adjunte el archivo PDF del telegrama enviado.</p>
            </div>

            {/* --- SECCIÓN ART --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <BusinessIcon fontSize="small" className="me-2 mb-1" />
                    Selección de Aseguradora (ART)
                </h6>
                <Autocomplete
                    options={aseguradoras}
                    getOptionLabel={(option) => option.nombre || ''}
                    value={aseguradoraSeleccionada}
                    onChange={(e, val) => setAseguradoraSeleccionada(val)}
                    isOptionEqualToValue={(option, value) => option.idAseguradora === value.idAseguradora}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Buscar Aseguradora..."
                            variant="outlined"
                            size="small"
                        />
                    )}
                    noOptionsText="No se encontraron aseguradoras"
                />
            </div>

            {/* --- SECCIÓN DE FECHA Y MÉDICO CITADO --- */}
            <div className="mt-4">
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label fw-semibold text-primary">
                            <DateRangeIcon fontSize="small" className="me-2 mb-1" />
                            Fecha de Envío del Telegrama
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            value={fechaEnvio}
                            onChange={(e) => setFechaEnvio(e.target.value)}
                        />
                        <small className="text-muted">Campo requerido</small>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label fw-semibold text-primary">
                            <PersonIcon fontSize="small" className="me-2 mb-1" />
                            Médico Citado en el Telegrama
                        </label>
                        <Autocomplete
                            options={medicos}
                            getOptionLabel={(option) => `${option.nombre} ${option.apellido || ''}`.trim()}
                            value={medicoSeleccionado}
                            onChange={(e, val) => setMedicoSeleccionado(val)}
                            isOptionEqualToValue={(option, value) => option.idMedico === value?.idMedico}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Buscar Médico..."
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            noOptionsText="No se encontraron médicos"
                        />
                        <small className="text-muted">Opcional</small>
                    </div>
                </div>
            </div>

            {/* --- SECCIÓN DE OBSERVACIONES --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <DescriptionIcon fontSize="small" className="me-2 mb-1" />
                    Observaciones
                </h6>
                <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Ingrese observaciones sobre la preparación o envío del telegrama..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                ></textarea>
                <small className="text-muted mt-1 d-block">Este comentario se guardará en el historial del trámite.</small>
            </div>

            {/* --- GESTOR DE ARCHIVOS --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary">Carga del Telegrama (PDF) u otros adjuntos</h6>
                {cargandoArchivos ? (
                    <p>Cargando archivos...</p>
                ) : (
                    <>
                        {archivos.length > 0 && (
                            <div className="list-group mb-3">
                                {archivos.map(archivo => (
                                    <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                            <span className="fw-semibold">{archivo.nombre}</span>
                                            <small className="text-muted ms-2">({archivo.tamaño})</small>
                                            <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : archivo.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                {archivo.estado}
                                            </span>
                                        </div>
                                        <div>
                                            {archivo.esDelBackend && (
                                                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleDescargarArchivo(archivo)} title="Descargar">
                                                    <DownloadIcon fontSize="small" />
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(archivo.id)} title="Eliminar">
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                            style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#cfe2ff' }}
                        >
                            <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                            <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                            <p className="mb-0">Arrastra archivos aquí o haz clic para adjuntar el telegrama en PDF</p>
                        </div>

                        {archivosPendientes && (
                            <div className="d-flex justify-content-center mt-3">
                                <button className="btn btn-primary rounded-pill px-4" onClick={handleSubirArchivos} disabled={subiendoArchivos}>
                                    {subiendoArchivos ? 'Subiendo...' : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- ACCIONES DE SALIDA --- */}
            <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={handleConfirmarEnvio}
                    disabled={cargando || subiendoArchivos || archivosPendientes}
                    title={archivosPendientes ? "Debe subir todos los archivos pendientes primero" : "Confirmar y avanzar a Telegrama Enviado"}
                >
                    <AssignmentTurnedInIcon fontSize="small" />
                    Confirmar Envío de Telegrama
                </button>
            </div>
        </div>
    );
}

export default GestorPreparandoTelegrama;

